import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { withRouter } from 'react-router-dom';

import useOnEsc from 'hooks/useOnEsc';
import useTrapFocus from 'hooks/useTrapFocus';
import useRandomId from 'hooks/useRandomId';
import useFocusOnOpen from 'hooks/useFocusOnOpen';
import VisuallyHidden from 'components/consumer/VisuallyHidden';
import getOrCreateElementById from 'commons/getOrCreateElementById';
import MobileModal from 'components/consumer/MobileModal';
import BodyScrollLock from 'components/consumer/BodyScrollLock';
import IconSimpleArrowLeft from 'components/icons/iconSimpleArrowLeft';
import IconClose from 'components/icons/iconClose';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import useScreen from 'hooks/useScreen';
import isNewConsumerApp from 'commons/isNewConsumerApp';

const fullScreenModalRootElement = getOrCreateElementById(
  'FullScreenModal-root'
);

const FullScreenModal = ({
  children,
  closeAlign = 'left',
  closeIcon,
  enableHashHistory = true,
  className = '',
  headerCenter,
  headerLeft,
  headerRight,
  hideBack,
  history,
  location = {},
  isOpen = false,
  mobileContent,
  mobileContentUnderHeader = null,
  mobileHeaderCenter,
  mobileHeaderNoPointerEvents = false,
  mobileHeaderRight,
  mobileHideHeader = false,
  mobileIsFullHeight = true,
  mobileNoPadding = false,
  mobileContentNoPadding = false,
  mobileNoStickyFooterPadding = false,
  mobileScrollingHeader,
  mobileStickyFooter,
  mobileVariant,
  modalPath = 'modal',
  onBack,
  onClose,
  stickyFooter,
  title,
  withUnderLay = false,
}) => {
  const { isMobile } = useScreen();

  const modalRef = useFocusOnOpen(isOpen);
  const modalTitleId = useRandomId('FullScreenModal-title');

  // If mobile, let MobileModal handle trapping of focus
  useTrapFocus(isOpen && !isMobile, modalRef);

  const [isHistoryPushed, setIsHistoryPushed] = useState(false);
  const locationStateString = JSON.stringify(location.state);

  useEffect(() => {
    if (
      !isMobile &&
      enableHashHistory &&
      history &&
      !isHistoryPushed &&
      isOpen
    ) {
      const locationState = JSON.parse(locationStateString || '{}');

      history.push(`${location.search || ''}#${modalPath}`, locationState);

      setIsHistoryPushed(true);
      if (isNewConsumerApp) {
        history.listen(() => {
          onClose();
        });
      } else {
        history.listen(() => {
          if (history.action === 'POP') {
            onClose();
          }
        });
      }
    }
  }, [
    isMobile,
    isOpen,
    enableHashHistory,
    history,
    isHistoryPushed,
    modalPath,
    onClose,
    locationStateString,
    location.search,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setIsHistoryPushed(false);
    }
  }, [isOpen]);

  const onCloseClick = () => {
    if (!isMobile && enableHashHistory && history && isHistoryPushed) {
      history.goBack();
    }
    onClose();
  };

  const checkUnderLayClick = event => {
    if (
      !withUnderLay ||
      typeof event?.target?.className?.includes !== 'function'
    ) {
      return;
    }
    if (event.target.className.includes('__with-underlay')) {
      onCloseClick();
    }
  };

  useOnEsc(isOpen, onCloseClick);

  if (!isOpen) {
    return null;
  }

  if (isMobile) {
    return (
      <MobileModal
        className={className}
        closeIcon={closeIcon}
        contentNoPadding={mobileContentNoPadding}
        contentUnderHeader={mobileContentUnderHeader}
        enableHashHistory={enableHashHistory}
        headerCenter={mobileHeaderCenter}
        headerNoPointerEvents={mobileHeaderNoPointerEvents}
        headerRight={mobileHeaderRight}
        hideHeader={mobileHideHeader}
        isFullHeight={mobileIsFullHeight}
        isOpen={isOpen}
        modalPath={modalPath}
        noPadding={mobileContentUnderHeader || mobileNoPadding}
        noStickyFooterPadding={mobileNoStickyFooterPadding}
        onClose={onCloseClick}
        scrollingHeader={mobileScrollingHeader}
        stickyFooter={mobileStickyFooter || stickyFooter}
        title={title}
        variant={mobileVariant}
      >
        {mobileContent || children}
      </MobileModal>
    );
  }

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={modalRef}
      role="dialog"
      aria-labelledby={modalTitleId}
      className={`${
        withUnderLay
          ? ' h-screen left-0 fixed top-0 w-screen bg-[rgba(0,0,0,.6)]'
          : ''
      }`}
      onClick={checkUnderLayClick}
    >
      <BodyScrollLock
        className={`bg-white flex flex-col z-[2000] h-screen left-0 fixed top-0 w-screen ${className}`}
      >
        <VisuallyHidden>Modal dialog content starts</VisuallyHidden>

        <div className="bg-white relative [border-bottom:1px_solid_#f1f1f1] flex h-[65px] p-4">
          <div className="items-center flex flex-1 flex-row z-[1]">
            {closeAlign === 'left' && (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                type="button"
                className="bg-transparent rounded-[3px] border-2 border-solid border-transparent cursor-pointer h-[49px] left-2 px-[7px] py-[13px] absolute top-2 w-[34px] z-[7]"
                onClick={onCloseClick}
              >
                <IconSimpleArrowLeft className="h-[49px] w-[30px] px-[7px] py-[13px] absolute z-[6] top-0 left-0 [&_path]:stroke-[#424242]" />
              </button>
            )}

            {!!onBack && !hideBack && (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                type="button"
                className="bg-transparent rounded-[3px] border-2 border-solid border-transparent cursor-pointer h-[49px] left-2 px-[7px] py-[13px] absolute top-2 w-[34px] z-[7]"
                onClick={onBack}
              >
                <IconSimpleArrowLeft className="h-[49px] w-[30px] px-[7px] py-[13px] absolute z-[6] top-0 left-0 [&_path]:stroke-[#424242]" />
              </button>
            )}

            <div className="items-center flex justify-between pl-[30px] w-full">
              <div className="z-[1]">{headerLeft}</div>

              <div className="h-full left-0 px-14 py-4 absolute right-0 text-center top-0 z-0 flex flex-col items-center justify-center">
                {headerCenter}
              </div>

              <div className="z-[1]">{headerRight}</div>
            </div>

            {closeAlign === 'right' && (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                type="button"
                className="bg-transparent rounded-[3px] border-2 border-solid border-transparent cursor-pointer h-[49px] left-2 px-[7px] py-[13px] absolute top-2 w-[34px] z-[7]"
                onClick={onCloseClick}
              >
                <IconClose className="h-[49px] w-[30px] px-[7px] py-[13px] absolute z-[6] top-0 right-0 [&_path]:fill-gray-light6 [&:hover_path]:fill-gray" />
              </button>
            )}
          </div>
        </div>

        <div
          className="bg-white relative flex-1 overflow-auto"
          id="FullScreen-content"
        >
          {title && (
            <div
              className="text-xl font-bold text-gray pt-0 px-0 pb-4"
              id={modalTitleId}
            >
              {title}
            </div>
          )}
          {children}
        </div>

        <div className="FullScreenModal__sticky-footer">{stickyFooter}</div>

        <VisuallyHidden>Modal dialog content ends</VisuallyHidden>
      </BodyScrollLock>
    </div>,
    fullScreenModalRootElement
  );
};

FullScreenModal.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  closeAlign: PropTypes.oneOf(['left', 'right']),
  closeIcon: PropTypes.node,
  enableHashHistory: PropTypes.bool,
  headerCenter: PropTypes.node,
  headerLeft: PropTypes.node,
  headerRight: PropTypes.node,
  hideBack: PropTypes.bool,
  history: PropTypes.shape({
    action: PropTypes.string,
    goBack: PropTypes.func,
    listen: PropTypes.func,
    push: PropTypes.func,
  }),
  isOpen: PropTypes.bool,
  location: PropTypes.object,
  mobileContent: PropTypes.node,
  mobileContentUnderHeader: PropTypes.bool,
  mobileHeaderCenter: PropTypes.node,
  mobileHeaderNoPointerEvents: PropTypes.bool,
  mobileHeaderRight: PropTypes.node,
  mobileHideHeader: PropTypes.bool,
  mobileIsFullHeight: PropTypes.bool,
  mobileNoPadding: PropTypes.bool,
  mobileContentNoPadding: PropTypes.bool,
  mobileNoStickyFooterPadding: PropTypes.bool,
  mobileScrollingHeader: PropTypes.node,
  mobileStickyFooter: PropTypes.node,
  mobileVariant: PropTypes.string,
  modalPath: PropTypes.string,
  onBack: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  stickyFooter: PropTypes.node,
  title: PropTypes.string,
  withUnderLay: PropTypes.bool,
};

export default withBrowserOnly(withRouter(FullScreenModal));
